import { Alert } from '@xefi/x-react/alert';
import { Button } from '@xefi/x-react/button';
import { Divider } from '@xefi/x-react/divider';
import { mergeTailwindClasses } from '@xefi/x-react/utils';
import DOMPurify from 'dompurify';
import { t } from 'i18next';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useGetSingleSurveyResponseReportMutation } from '@/app/survey/_hooks/useGetSingleSurveyResponseReportMutation';
import handClapAnimation from '@/assets/lottie/hand-clap.json';
import notSentAnimation from '@/assets/lottie/not-sent.json';
import sentAnimation from '@/assets/lottie/sent.json';
import { useResendEmailMutation } from '@/hooks/useResendEmailMutation';
import { useToasts } from '@/hooks/useToasts';
import type { SurveyTaking } from '@/models/survey';
import { downloadMedia } from '@/utils/common';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

type SurveyTakingEndPageProps = {
   currentSurvey?: SurveyTaking;
   valid: boolean;
   onRetry: VoidFunction;
   entryId?: string;
   entryIdParam?: string;
   isPreview: boolean;
};

export const SurveyTakingEndPage = ({
   currentSurvey,
   valid,
   onRetry,
   entryId,
   entryIdParam,
   isPreview,
}: SurveyTakingEndPageProps) => {
   const { addErrorToast } = useToasts();

   const resendEmailMutation = useResendEmailMutation();
   const getSingleSurveyResponseReportMutation =
      useGetSingleSurveyResponseReportMutation();

   const onResend = async () => {
      if (!entryId) return;

      resendEmailMutation.mutate({ entryId });
   };

   const handleShowResult = (responseId: string) => {
      getSingleSurveyResponseReportMutation.mutate(
         {
            responseId,
            surveyId: currentSurvey?.id,
         },
         {
            onSuccess: (response) => {
               downloadMedia(
                  response,
                  `response-report-${responseId}.pdf`,
                  'application/pdf',
               );
            },
            onError: () => {
               addErrorToast(
                  t('response.pdfDownloaded.error', { ns: 'notifications' }),
               );
            },
         },
      );
   };

   if (valid && !entryId) {
      return (
         <div className='flex grow flex-col items-center justify-center gap-8'>
            {isPreview && (
               <Alert
                  className='w-2/4'
                  title={t('survey.surveyNotSend')}
                  classNames={{
                     base: '!bg-warning/20 border !border-warning/10 grow-0',
                     alertIcon: 'text-warning/70',
                     closeButton: 'hidden',
                  }}
                  description={t('survey.isPreview')}
               />
            )}
            <Lottie
               animationData={sentAnimation}
               loop={true}
               style={{
                  height: 273,
               }}
            />
            <h3 className='text-xl font-bold opacity-40'>
               {t('survey.finish.header')}
            </h3>
            <div className='w-full px-10'>
               {currentSurvey?.end_page_message ? (
                  <div
                     className='w-full'
                     style={{ minHeight: '0' }}
                     dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                           currentSurvey?.end_page_message || '',
                        ),
                     }}
                  ></div>
               ) : (
                  <div className='flex w-full justify-center text-sm'>
                     <p className='max-w-[650px] text-center'>
                        {t('survey.finish.text')}
                     </p>
                  </div>
               )}
            </div>
            {currentSurvey?.type.slug === 'graded' && (
               <div
                  className={mergeTailwindClasses(
                     currentSurvey?.has_results_showed &&
                        'grid w-full grid-cols-[1fr_auto_1fr] gap-6',
                  )}
               >
                  {currentSurvey?.has_results_showed && (
                     <>
                        <button
                           onClick={() => {
                              if (!entryIdParam) return;
                              handleShowResult(entryIdParam);
                           }}
                           className='cursor-pointer justify-self-end text-sm text-[#06AEF5] underline'
                        >
                           {t('survey.graded.showResult')}
                        </button>
                        <Divider orientation='vertical' />
                     </>
                  )}
                  <Link
                     href={`${process.env.PORTAIL_URL}/auth?step=LegalNotices`}
                     className='text-sm text-[#06AEF5] underline'
                  >
                     {t('survey.graded.showLegalNotices')}
                  </Link>
               </div>
            )}
         </div>
      );
   }

   if (!valid && entryId) {
      return (
         <div className='flex h-[830px] flex-col items-center justify-center'>
            <Lottie
               animationData={handClapAnimation}
               loop={false}
               style={{
                  height: 273,
               }}
            />
            <h3 className='text-secondary mt-11 mb-4 text-xl font-bold'>
               {t('error.already.header')}
            </h3>

            <div className='max-w-[1000px] p-[29px] text-center text-[12px]'>
               <p>{t('error.already.text1')}</p>
               <p>{t('error.already.text2')}</p>
               <p>{t('resend.regenerate')}</p>
               <br />
            </div>
            <Button onClick={onResend}>
               {t('resend.email').toUpperCase()}
            </Button>
            <p className='mt-4'>{t('survey.footer.thanks')}</p>
         </div>
      );
   }

   return (
      <div className='flex h-[830px] flex-col items-center justify-center'>
         <Lottie
            animationData={notSentAnimation}
            loop={false}
            style={{
               height: 273,
            }}
         />
         <h3 className='text-secondary mt-11 mb-4 text-xl font-bold'>
            {t('sendError.header')}
         </h3>

         <div className='max-w-[650px] p-[30px] text-center text-sm whitespace-pre-line'>
            {t('sendError.header')}
         </div>
         <Button onClick={onRetry}>{t('resend.label')}</Button>
      </div>
   );
};
